import { plural, t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { ReactNode } from "react";

import { Button } from "./button";
import { DialogFooter, DialogHeader, useDialogClose } from "./dialog";
import { useDialog } from "./dialog-provider";
import { DialogService } from "./dialog-service";

/**
 * The card, title and action row shared by the dialogs that add or correct
 * patient records. They are one visual family, and these parts were duplicated
 * verbatim in every one of them, so the ones written later drifted into a
 * different look. Only the chrome lives here.
 */

/**
 * The add dialogs' card only — the record editor sets its own, deliberately
 * narrower one (see `EDIT_DIALOG_CLASS`).
 */
export const ADD_DIALOG_CLASS = "max-h-[90dvh]";

/**
 * Desktop width for the add dialogs, passed as `Dialog`'s `width` prop rather
 * than a `w-` class: the class would outrank the `max-sm:` full-bleed rule and
 * leave the dialog overflowing a phone screen (see `dialog.tsx`).
 */
export const ADD_DIALOG_WIDTH = "620px";

/**
 * Card for the dialogs that lay several columns of controls side by side (the
 * permissions dialog and the new-user wizard's permissions step). Centred like
 * every other dialog; capped at the viewport, with the content scrolling inside.
 *
 * Passed as `className`, not alongside `width` - the `sm:max-w-` here is what
 * lifts `DialogContent`'s own 512px cap.
 */
export const WIDE_DIALOG_CLASS = "max-h-[90dvh] w-full sm:max-w-280";

/**
 * The props a record dialog grows when the extraction reviewer opens it.
 * `onCollect` hands the built records back instead of saving them (they are
 * ingested later with the rest of a document's approved entities).
 * `patientId` names the owner for callers with no patient loaded — the review
 * queue works across patients. Defaults to the app's current patient.
 * `docked` keeps the document the records are read from in view, and turns on
 * the review forms' extras: the incomplete-row marker, a list that does not
 * shrink, and the discard prompt.
 * `recordedAt` dates a new reading, for dialogs whose records carry one.
 * Defaults to now.
 */
export interface CollectModeProps<T> {
  onCollect?: (items: T[]) => void;
  patientId?: number;
  docked?: boolean;
  recordedAt?: string;
}

export function RecordDialogTitle({ children }: { children: ReactNode }) {
  return <DialogHeader>{children}</DialogHeader>;
}

/**
 * The footer: Cancel, then the submit, on the right — like every dialog. The
 * record edit dialog uses it too, though its card is narrower. Cancel is the
 * dialog's own close, so it goes the same way as Escape and a click outside.
 */
export function RecordDialogActions({
  disabled,
  started,
  children,
}: {
  /** The caller's own validity check. */
  disabled?: boolean;
  started?: () => number;
  /** The submit button's label — "Add" or "Save", plus any count. */
  children: ReactNode;
}) {
  const { confirm } = useDialog();
  const onCancel = useDialogClose(
    started &&
      (() => {
        const n = started();
        return (
          n === 0 ||
          confirmDiscard(
            confirm,
            plural(n, {
              one: "Discard # unsaved row?",
              other: "Discard # unsaved rows?",
            }),
            t`What you entered will be lost.`,
          )
        );
      }),
  );

  return (
    <DialogFooter>
      <Button type="button" onClick={onCancel}>
        <Trans>Cancel</Trans>
      </Button>
      <Button variant="primary" type="submit" disabled={disabled}>
        {children}
      </Button>
    </DialogFooter>
  );
}

/** Asks before dropping what the user entered; resolves true to drop it. */
export function confirmDiscard(
  confirm: DialogService["confirm"],
  title: string,
  message: string,
) {
  return confirm(message, title, {
    confirmLabel: t`Discard`,
    cancelLabel: t`Keep editing`,
    destructive: true,
  });
}
